import ipaddr from 'ipaddr.js';

import type { PostgresConnectionConfig, PostgresSslMode } from '../types.js';
import { POSTGRES_SSL_MODES } from './postgres-ssl-mode.js';
import { isUnsafeIpLiteral, normalizeIPv4Literal } from './private-ip.js';
import { isSafeSqlIdentifier } from './redshift-connection.js';
import { pickPortString, pickString } from './redshift-rs-config.js';
import type { WarehouseHostValidationOptions } from './warehouse-host-validation.js';
import { isValidDnsHostname } from './warehouse-host-validation.js';

export { isSafeSqlIdentifier };

export const DEFAULT_POSTGRES_PORT = 5432;

export function parsePostgresSslMode(raw: unknown): PostgresSslMode | undefined {
  if (typeof raw !== 'string') return undefined;
  const mode = raw.toLowerCase();
  return POSTGRES_SSL_MODES.find((candidate) => candidate === mode);
}

// Stored URLs follow sqlconnect-go: absent/null/blank means disable; unknown modes stay undefined for caller fallback.
export function effectivePostgresSslMode(raw: unknown): PostgresSslMode | undefined {
  if (raw === undefined || raw === null || (typeof raw === 'string' && raw.trim() === '')) return 'disable';
  return parsePostgresSslMode(raw);
}

const PROBE_URL_HOST = '_';

const PG_HOST_KEYS = ['host'] as const;
const PG_PORT_KEYS = ['port'] as const;
const PG_USER_KEYS = ['user', 'username'] as const;
const PG_NAMESPACE_KEYS = ['namespace'] as const;

/** Generic hostname check — non-empty, no URL delimiters, round-trips through URL.hostname. */
export function isValidPostgresHost(host: string, options: WarehouseHostValidationOptions = {}): boolean {
  const h = host.trim();
  if (!h || h.length > 253) return false;
  const bareHost = h.startsWith('[') && h.endsWith(']') ? h.slice(1, -1) : h;
  const allowPrivate = options.allowPrivate ?? false;
  if (allowPrivate && (h.toLowerCase() === 'localhost' || ipaddr.isValid(bareHost))) return true;
  if (h.toLowerCase() === 'localhost') return false;
  if (/\s/.test(h)) return false;
  if (/[@:/?#\\]/.test(h)) return false;
  // Public IPv6 literals are intentionally unsupported at save — use a DNS hostname instead.
  if (h.startsWith('[') && h.endsWith(']')) return false;

  const ipv4 = normalizeIPv4Literal(h);
  if (ipv4 === null && !isValidDnsHostname(h)) return false;

  const probe = new URL(`postgresql://${PROBE_URL_HOST}/`);
  try {
    probe.hostname = h;
  } catch {
    return false;
  }
  const normalized = probe.hostname.toLowerCase();
  if (normalized !== h.toLowerCase() || normalized === PROBE_URL_HOST) return false;
  if (ipv4 !== null && ipv4 !== h) return false;
  if (isUnsafeIpLiteral(h)) return false;
  return true;
}

/** Prefill fields derived from RS config; port omitted when RS did not supply one. */
export type PostgresPrefillConfig = Omit<PostgresConnectionConfig, 'port' | 'password' | 'sslmode'> & {
  port?: number;
  rsSslMode?: string;
};

/** Derive form prefill from sanitized RudderStack destination config. */
export function postgresPrefillFromRsConfig(
  rsConfig: Record<string, string | undefined> | null | undefined,
): PostgresPrefillConfig {
  const rsRecord = (rsConfig ?? {}) as Record<string, unknown>;
  const host = pickString(rsRecord, PG_HOST_KEYS) ?? '';
  const portRaw = pickPortString(rsRecord, PG_PORT_KEYS) ?? '';
  const database = pickString(rsRecord, ['database']) ?? '';
  const user = pickString(rsRecord, PG_USER_KEYS) ?? '';
  const namespace = pickString(rsRecord, PG_NAMESPACE_KEYS) ?? '';
  const port = portRaw ? Number(portRaw) : undefined;
  const rsSslMode = pickString(rsRecord, ['sslMode']);

  return {
    host,
    ...(port !== undefined && Number.isFinite(port) ? { port } : {}),
    database,
    user,
    schema: namespace.trim() || undefined,
    ...(rsSslMode !== undefined ? { rsSslMode } : {}),
  };
}
